/**
 * Dimensional algebra over the FOUR base dimensions of the coverage engine —
 * TIME, PERSON, EVENT, MONEY.
 *
 * ── WHY `EVENT` EXISTS, AND WHY THE MODULE IS WORTHLESS WITHOUT IT ──────────
 *
 * A coverage percentage is `person-hours automated ÷ person-hours worked`. Both
 * sides are built from a frequency and a duration:
 *
 *     ticketsPerWeek : EVENT             40 tickets, in one week
 *     hoursPerTicket : TIME · EVENT⁻¹    12 minutes each
 *     product        : TIME              8 hours, in one week
 *     × weeksPerYear : (dimensionless)   × 46
 *     × headcount    : PERSON            × 12
 *                    = TIME · PERSON     person-hours per year
 *
 * Delete the EVENT dimension and `40 tickets` and `12 people` are both plain
 * counts. Forget the `hoursPerTicket` factor and the expression still
 * multiplies cleanly, still yields a number, still renders as a confident
 * percentage — and means nothing. With EVENT, that same mistake leaves an
 * EVENT¹ residue in the result, the top-level check that the numerator and
 * denominator are both `TIME·PERSON` fails, and the model NAMES ITSELF instead
 * of quietly shipping a fabricated ROI figure to a paying customer.
 *
 * `hours + headcount` is rejected for the same reason, and it is rejected when
 * the model is WRITTEN, not when it is read.
 *
 * ── THE TWO ALGEBRAS, AND EXACTLY WHERE EACH ONE APPLIES ────────────────────
 *
 * There are two, on purpose, because the module has two kinds of caller:
 *
 * 1. COMPILE TIME — `CoverageDim<T, P, E, M>` carries the four exponents as
 *    literal types, and `CoverageDimensionProduct` / `CoverageDimensionQuotient` compute over
 *    them. This is what the builder in `coverage-formula.builder.ts` uses, so
 *    TypeScript rejects `add(hours, people)` before the code runs.
 * 2. RUNTIME — `CoverageDimension` carries the same four exponents as plain
 *    numbers, and `multiplyCoverageDimensions` / `coverageDimensionsEqual` compute over them.
 *    This is what the evaluator uses, because a model parsed out of stored JSON
 *    has no literal types to reason about.
 *
 * The two are pinned against each other by `coverage-dimension.test.ts`: any
 * divergence between the type-level and value-level arithmetic fails the suite.
 *
 * WHERE THE COMPILE-TIME GUARANTEE STOPS. Exponents are exact over
 * `[-6, +6]`. That is far past anything meaningful — `TIME·PERSON` is the
 * largest shape this engine produces, and an exponent of 2 is already a
 * modelling bug. Past that range the type-level sum evaluates to `never`; the
 * builder detects that and hands back `CoverageDimensionOverflow`, a type
 * nothing accepts, so the call site fails to compile rather than silently
 * widening. Parsed JSON gets no compile-time checking at all by construction —
 * `coverage-formula.schema.ts` refuses an out-of-range exponent at parse time
 * and the evaluator raises `EXPONENT_OUT_OF_RANGE` if one is ever produced
 * mid-computation.
 */

/** The four base dimensions, in the canonical order used by every printer. */
export const COVERAGE_DIMENSION_AXES = ["time", "person", "event", "money"] as const;

/** One of the four base dimensions. */
export type CoverageDimensionAxis = (typeof COVERAGE_DIMENSION_AXES)[number];

/** Lowest exponent the algebra represents, in both the type and value worlds. */
export const COVERAGE_EXPONENT_MIN = -6;

/** Highest exponent the algebra represents, in both the type and value worlds. */
export const COVERAGE_EXPONENT_MAX = 6;

/**
 * A dimension as the EVALUATOR sees it: four plain exponents.
 *
 * Used for anything derived from stored JSON, where literal types do not exist.
 */
export interface CoverageDimension {
  readonly time: number;
  readonly person: number;
  readonly event: number;
  readonly money: number;
}

/** The exponents the compile-time algebra represents exactly. */
export type CoverageExponent = -6 | -5 | -4 | -3 | -2 | -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * A dimension as the BUILDER sees it: four exponents as literal types.
 *
 * Structurally a `CoverageDimension`, so a value of this type is accepted
 * anywhere the runtime algebra is used, with no conversion and no cast.
 */
export interface CoverageDim<
  T extends CoverageExponent,
  P extends CoverageExponent,
  E extends CoverageExponent,
  M extends CoverageExponent
> {
  readonly time: T;
  readonly person: P;
  readonly event: E;
  readonly money: M;
}

/** Any dimension with literal exponents. The constraint used by the builder. */
export type AnyCoverageDim = CoverageDim<
  CoverageExponent,
  CoverageExponent,
  CoverageExponent,
  CoverageExponent
>;

/* ────────────────────────────────────────────────────────────────────────────
 * Compile-time exponent arithmetic.
 *
 * TypeScript has no type-level integers, so signed exponents are mapped onto
 * tuple LENGTHS — the only arithmetic the type system natively performs. Each
 * exponent `n` is represented by the index `n + 6` in `[-6 … 6]`; addition is
 * `(a + 6) + (b + 6) - 6`, computed by concatenating and slicing tuples. Any
 * index that leaves `0 … 12` is not a valid position, so the result is `never`
 * — overflow is unrepresentable rather than wrapped or clamped.
 * ──────────────────────────────────────────────────────────────────────────── */

type Tuple<N extends number, Acc extends readonly unknown[] = []> = Acc["length"] extends N
  ? Acc
  : Tuple<N, [...Acc, unknown]>;

type NatAdd<A extends number, B extends number> = [...Tuple<A>, ...Tuple<B>]["length"] & number;

type NatSub<A extends number, B extends number> =
  Tuple<A> extends readonly [...Tuple<B>, ...infer Rest] ? Rest["length"] : never;

type ExponentIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

type ExponentByIndex = readonly [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6];

type FromIndex<I> = I extends ExponentIndex ? ExponentByIndex[I] : never;

type ToIndex<E extends CoverageExponent> = E extends -6
  ? 0
  : E extends -5
    ? 1
    : E extends -4
      ? 2
      : E extends -3
        ? 3
        : E extends -2
          ? 4
          : E extends -1
            ? 5
            : E extends 0
              ? 6
              : E extends 1
                ? 7
                : E extends 2
                  ? 8
                  : E extends 3
                    ? 9
                    : E extends 4
                      ? 10
                      : E extends 5
                        ? 11
                        : E extends 6
                          ? 12
                          : never;

/** `A + B`, or `never` when the sum leaves `[-6, +6]`. */
export type CoverageExponentSum<A extends CoverageExponent, B extends CoverageExponent> = FromIndex<
  NatSub<NatAdd<ToIndex<A>, ToIndex<B>>, 6>
>;

/** `-E`. Total over the representable range: the range is symmetric. */
export type CoverageExponentNegate<E extends CoverageExponent> = FromIndex<NatSub<12, ToIndex<E>>>;

/** `A - B`, or `never` when the difference leaves `[-6, +6]`. */
export type CoverageExponentDifference<A extends CoverageExponent, B extends CoverageExponent> =
  CoverageExponentNegate<B> extends CoverageExponent
    ? CoverageExponentSum<A, CoverageExponentNegate<B>>
    : never;

/**
 * Assemble a dimension, collapsing the WHOLE dimension to `never` if any single
 * exponent overflowed.
 *
 * Collapsing all four matters: `CoverageDim<never, 0, 0, 0>` would still be a
 * structurally valid dimension, and `never` is assignable to everything, so an
 * overflowed exponent would silently unify with any other dimension. One
 * `never` for the whole vector is a value the builder can detect and refuse.
 */
type DimOrNever<T, P, E, M> = [T] extends [never]
  ? never
  : [P] extends [never]
    ? never
    : [E] extends [never]
      ? never
      : [M] extends [never]
        ? never
        : T extends CoverageExponent
          ? P extends CoverageExponent
            ? E extends CoverageExponent
              ? M extends CoverageExponent
                ? CoverageDim<T, P, E, M>
                : never
              : never
            : never
          : never;

/** The dimension of `a × b`: exponents add. `never` on overflow. */
export type CoverageDimensionProduct<
  A extends AnyCoverageDim,
  B extends AnyCoverageDim
> = DimOrNever<
  CoverageExponentSum<A["time"], B["time"]>,
  CoverageExponentSum<A["person"], B["person"]>,
  CoverageExponentSum<A["event"], B["event"]>,
  CoverageExponentSum<A["money"], B["money"]>
>;

/** The dimension of `a ÷ b`: exponents subtract. `never` on overflow. */
export type CoverageDimensionQuotient<
  A extends AnyCoverageDim,
  B extends AnyCoverageDim
> = DimOrNever<
  CoverageExponentDifference<A["time"], B["time"]>,
  CoverageExponentDifference<A["person"], B["person"]>,
  CoverageExponentDifference<A["event"], B["event"]>,
  CoverageExponentDifference<A["money"], B["money"]>
>;

/** The dimension of a left-to-right product of a tuple of dimensions. */
export type CoverageDimensionProductAll<Ds extends readonly AnyCoverageDim[]> =
  Ds extends readonly [
    infer Head extends AnyCoverageDim,
    ...infer Rest extends readonly AnyCoverageDim[]
  ]
    ? Rest extends readonly []
      ? Head
      : CoverageDimensionProductAll<Rest> extends infer Tail
        ? [Tail] extends [never]
          ? never
          : Tail extends AnyCoverageDim
            ? CoverageDimensionProduct<Head, Tail>
            : never
        : never
    : CoverageDim<0, 0, 0, 0>;

/* ────────────────────────────────────────────────────────────────────────────
 * Constructors and the named catalog.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Build a dimension whose exponents survive as literal types.
 *
 * Deliberately positional and deliberately cast-free: the parameters are the
 * type variables, so the returned object literal satisfies `CoverageDim<T, P, E, M>`
 * on its own. There is no `as` anywhere in this module's dimension path.
 */
export function coverageDim<
  T extends CoverageExponent,
  P extends CoverageExponent,
  E extends CoverageExponent,
  M extends CoverageExponent
>(time: T, person: P, event: E, money: M): CoverageDim<T, P, E, M> {
  return { time, person, event, money };
}

/**
 * The dimensions a coverage model actually uses, named.
 *
 * Not a closed set — `coverageDim(…)` builds any other — but naming the common ones
 * keeps an authored model readable and keeps the exponents in one place.
 */
export const CoverageDimensions = {
  /** A pure ratio, count or fraction: attribution weights, weeks-per-year. */
  DIMENSIONLESS: coverageDim(0, 0, 0, 0),
  /** A duration. Canonically HOURS everywhere inside the evaluator. */
  HOURS: coverageDim(1, 0, 0, 0),
  /** A headcount. */
  PEOPLE: coverageDim(0, 1, 0, 0),
  /** A count of discrete work items: tickets, orders, calls, documents. */
  EVENTS: coverageDim(0, 0, 1, 0),
  /** An amount of money, in the organisation's currency. */
  MONEY: coverageDim(0, 0, 0, 1),
  /** The dimension every coverage numerator and denominator must reduce to. */
  PERSON_HOURS: coverageDim(1, 1, 0, 0),
  /** A handling time: hours per work item. */
  HOURS_PER_EVENT: coverageDim(1, 0, -1, 0),
  /** A rate: work items per hour. */
  EVENTS_PER_HOUR: coverageDim(-1, 0, 1, 0),
  /** A unit economic: money per work item. */
  MONEY_PER_EVENT: coverageDim(0, 0, -1, 1),
  /** A loaded cost: money per hour. */
  MONEY_PER_HOUR: coverageDim(-1, 0, 0, 1)
} as const;

/* ────────────────────────────────────────────────────────────────────────────
 * Runtime algebra. Used by the evaluator, which never sees literal types.
 * ──────────────────────────────────────────────────────────────────────────── */

/** True when two dimensions are the same on all four axes. */
export function coverageDimensionsEqual(a: CoverageDimension, b: CoverageDimension): boolean {
  return a.time === b.time && a.person === b.person && a.event === b.event && a.money === b.money;
}

/** The dimension of `a × b`. May be out of range; check with `isRepresentableCoverageDimension`. */
export function multiplyCoverageDimensions(
  a: CoverageDimension,
  b: CoverageDimension
): CoverageDimension {
  return {
    time: a.time + b.time,
    person: a.person + b.person,
    event: a.event + b.event,
    money: a.money + b.money
  };
}

/** The dimension of `a ÷ b`. May be out of range; check with `isRepresentableCoverageDimension`. */
export function divideCoverageDimensions(
  a: CoverageDimension,
  b: CoverageDimension
): CoverageDimension {
  return {
    time: a.time - b.time,
    person: a.person - b.person,
    event: a.event - b.event,
    money: a.money - b.money
  };
}

/** True when every exponent is an integer inside `[-6, +6]`. */
export function isRepresentableCoverageDimension(d: CoverageDimension): boolean {
  return COVERAGE_DIMENSION_AXES.every((axis) => {
    const exponent = d[axis];
    return (
      Number.isInteger(exponent) &&
      exponent >= COVERAGE_EXPONENT_MIN &&
      exponent <= COVERAGE_EXPONENT_MAX
    );
  });
}

/** True when the dimension has no exponent at all: a pure number. */
export function isCoverageDimensionless(d: CoverageDimension): boolean {
  return d.time === 0 && d.person === 0 && d.event === 0 && d.money === 0;
}

/**
 * A stable, locale-free rendering of a dimension, for warning messages.
 *
 * `dimensionless`, or the non-zero axes in canonical order: `time^1·person^1`.
 * Deterministic by construction — no `Intl`, no `toLocaleString`, fixed order.
 */
export function formatCoverageDimension(d: CoverageDimension): string {
  const parts = COVERAGE_DIMENSION_AXES.filter((axis) => d[axis] !== 0).map(
    (axis) => `${axis}^${d[axis]}`
  );
  return parts.length === 0 ? "dimensionless" : parts.join("·");
}
